import {
  AdminAnalyticsSection,
  AdminAnalyticsSegmentFilter,
  AdminAnalyticsStartedBy,
  type AdminAnalyticsReportFieldsFragment,
  type GetAdminAnalyticsAgencyProfileOptionsQuery
} from '~/api/graphql';
import { pluralize } from '~/lib/pluralize';
import type { AgencyProfileSelection } from '~/components/settings/analytics/use-analytics-filters';

export const SEGMENT_FILTER_NAMES: Record<AdminAnalyticsSegmentFilter, string> = {
  [AdminAnalyticsSegmentFilter.User]: 'User',
  [AdminAnalyticsSegmentFilter.AgencyProfile]: 'Access profile',
  [AdminAnalyticsSegmentFilter.StartedBy]: 'Started by',
  [AdminAnalyticsSegmentFilter.WorkspacePreset]: 'Workspace type'
};

/** In the Started by picker's order. */
export const STARTED_BY_OPTIONS: { value: AdminAnalyticsStartedBy; label: string }[] = [
  { value: AdminAnalyticsStartedBy.Manual, label: 'Manual' },
  { value: AdminAnalyticsStartedBy.ServiceTicket, label: 'Service ticket' },
  { value: AdminAnalyticsStartedBy.Email, label: 'Email' },
  { value: AdminAnalyticsStartedBy.Zapier, label: 'Zapier' },
  { value: AdminAnalyticsStartedBy.Trigger, label: 'Trigger' }
];

export function startedByLabel(startedBy: AdminAnalyticsStartedBy): string {
  return STARTED_BY_OPTIONS.find((option) => option.value === startedBy)?.label ?? startedBy;
}

export type AnalyticsProfileOption =
  GetAdminAnalyticsAgencyProfileOptionsQuery['adminAnalyticsAgencyProfileOptions'][number];

const NO_PROFILE_LABEL = 'No profile';

export function profileOptionLabel(option: AnalyticsProfileOption): string {
  if (option.agencyProfileId == null) return NO_PROFILE_LABEL;
  return option.isDefault ? `${option.name} (default)` : (option.name ?? '');
}

/**
 * Labels a picked profile, falling back when its option isn't loaded, so the chip that removes it
 * still shows while the options load or after they fail to.
 */
export function profileSelectionLabel(
  selection: AgencyProfileSelection,
  option: AnalyticsProfileOption | undefined
): string {
  if (option) return profileOptionLabel(option);
  return selection.kind === 'none' ? NO_PROFILE_LABEL : 'Selected profile';
}

export function profileSelectionOf(option: AnalyticsProfileOption): AgencyProfileSelection {
  return option.agencyProfileId == null
    ? { kind: 'none' }
    : { kind: 'profile', agencyProfileId: option.agencyProfileId };
}

export function findProfileOption(
  options: readonly AnalyticsProfileOption[],
  selection: AgencyProfileSelection | undefined
): AnalyticsProfileOption | undefined {
  if (!selection) return undefined;
  return options.find((option) =>
    selection.kind === 'none'
      ? option.agencyProfileId == null
      : option.agencyProfileId === selection.agencyProfileId
  );
}

export type UnfilteredSection = AdminAnalyticsReportFieldsFragment['unfilteredSections'][number];

const SECTION_NOUNS: Record<AdminAnalyticsSection, string> = {
  [AdminAnalyticsSection.Calls]: 'Calls',
  [AdminAnalyticsSection.VoiceAiAmsWrites]: 'Voice AI writes',
  [AdminAnalyticsSection.RevenueServiceTickets]: 'Quote request tickets',
  [AdminAnalyticsSection.Emails]: 'Emails',
  [AdminAnalyticsSection.KnowledgeBaseUploads]: 'Knowledge base uploads',
  [AdminAnalyticsSection.KnowledgeBaseHistory]: 'Knowledge base library totals',
  [AdminAnalyticsSection.TemplateLibrary]: 'Template library totals',
  [AdminAnalyticsSection.ActiveUsers]: 'Team adoption numbers'
};

const SEGMENT_FILTER_PHRASES: Record<AdminAnalyticsSegmentFilter, string> = {
  [AdminAnalyticsSegmentFilter.User]: 'user',
  [AdminAnalyticsSegmentFilter.AgencyProfile]: 'access profile',
  [AdminAnalyticsSegmentFilter.StartedBy]: 'how they started',
  [AdminAnalyticsSegmentFilter.WorkspacePreset]: 'workspace type'
};

function joinWords(words: string[], conjunction: 'and' | 'or'): string {
  if (words.length <= 1) return words.join('');
  return `${words.slice(0, -1).join(', ')} ${conjunction} ${words[words.length - 1]}`;
}

/**
 * The note shown in place of a section the filters can't apply to, naming the filters to
 * clear, e.g. "Calls can't be filtered by access profile yet. Clear the Access profile
 * filter to see them."
 */
export function unfilteredSectionNote({ section, filters }: UnfilteredSection): string {
  const phrases = joinWords(
    filters.map((filter) => SEGMENT_FILTER_PHRASES[filter]),
    'or'
  );
  const names = joinWords(
    filters.map((filter) => SEGMENT_FILTER_NAMES[filter]),
    'and'
  );
  const filterWord = pluralize(filters.length, 'filter');
  return `${SECTION_NOUNS[section]} can't be filtered by ${phrases} yet. Clear the ${names} ${filterWord} to see them.`;
}

export const CURRENT_PROFILE_HINT = "Grouped by each user's current profile.";

/** The Team performance line under an Access profile filter, e.g. "12 of 40 Producers active". */
export function profileAdoptionSummary(
  option: AnalyticsProfileOption,
  activeUsers: number,
  totalUsers: number
): string {
  const group = option.agencyProfileId == null ? 'users with no profile' : option.name;
  return `${activeUsers} of ${totalUsers} ${group} active`;
}
